import { 
  SalesMetric, 
  PipelineStage, 
  TopDeal, 
  Activity, 
  Business, 
  Notification, 
  AuthUser, 
  Group, 
  GroupMessages, 
  GroupMembers,
  SalesStats,
  User,
  SalesProject,
  WorkflowStage,
  ProjectNameApproval,
  StageGate,
  Approval,
  ContactInfo
} from '@/types';

// Extended types for the Sales-Ops-Management system
export interface WorkflowStage {
  id: string;
  stage_number: number;
  name: string;
  description: string;
  owner: string;
  rationale: string;
  timeline: string;
  icon: string;
  color: string;
  tasks: string[];
}

export interface ContactInfo {
  email: string;
  phone: string;
  address?: string;
  city: string;
  country: string;
}

export interface Lead {
  id: string;
  company_name: string;
  contact_person: string;
  email: string;
  phone: string;
  industry: string;
  source: 'referral' | 'website' | 'cold_call' | 'partner' | 'other';
  status: 'new' | 'contacted' | 'qualified' | 'disqualified';
  budget_range: string;
  urgency: 'high' | 'medium' | 'low';
  decision_makers: string[];
  notes: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  current_stage: number;
  progress: number;
  type?: 'talent_placement' | 'app_deployment';
  value?: string;
  team?: {
    sales: string[];
    ops: string[];
    management: string[];
  };
  location: string;
}

export interface ProjectNameApproval {
  id: string;
  project_id: string;
  requested_name: string;
  requested_by: string;
  approved: boolean;
  approved_by?: string;
  approved_at?: string;
  comments: string;
}

export interface StageGate {
  project_id: string;
  stage: number;
  requirements: {
    client_info_required: boolean;
    project_name_required: boolean;
    budget_approved: boolean;
    contract_signed: boolean;
  };
  approvals: Approval[];
  blockers: string[];
}

export interface Approval {
  id: string;
  type: 'project_name' | 'budget' | 'contract';
  approved: boolean;
  approved_by?: string;
  approved_at?: string;
  comments: string;
}

export interface SalesProject {
  id: string;
  name: string;
  client: string;
  type: 'talent_placement' | 'app_deployment';
  current_stage: number;
  progress: number;
  start_date: string;
  expected_completion: string;
  value: string;
  status: 'on_track' | 'at_risk' | 'delayed' | 'completed';
  team: {
    sales: string[];
    ops: string[];
    management: string[];
  };
  ownership_type: 'company_owned' | 'partner_submitted';
  submitted_by?: string;
  client_contact_info?: ContactInfo;
  project_name_approved: boolean;
  lead_id?: string;
  is_lead: boolean;
  location: string;
  currency: 'UGX' | 'USD';
}

export interface ProjectTimeline {
  stage: number;
  day_range: string;
  status: 'completed' | 'in_progress' | 'pending' | 'delayed';
  completion_date?: string;
  notes?: string;
}

export interface UserRole {
  can_create_leads: boolean;
  can_submit_projects: boolean;
  can_approve_project_names: boolean;
  can_move_to_contracting: boolean;
  view_company_owned_only: boolean;
  view_all_projects: boolean;
  can_manage_documents: boolean;
  can_view_financials: boolean;
}

// Stage Validation Rules
export const stageValidationRules = {
  1: {
    required: ['company_name', 'contact_person', 'source'],
    project_name_allowed: false,
    is_lead_stage: true
  },
  2: {
    required: ['budget_range', 'decision_makers', 'solution_type'],
    project_name_allowed: false,
    is_lead_stage: false
  },
  3: {
    required: ['proposal_document', 'pricing'],
    project_name_allowed: false,
    is_lead_stage: false
  },
  4: {
    required: ['contract_signed', 'project_name', 'kickoff_scheduled'],
    project_name_allowed: true,
    project_name_required: true,
    is_lead_stage: false
  },
  5: {
    required: ['delivery_plan', 'resource_allocation'],
    project_name_allowed: true,
    is_lead_stage: false
  },
  6: {
    required: ['client_feedback', 'performance_metrics'],
    project_name_allowed: true,
    is_lead_stage: false
  }
};

// WORKFLOW STAGES - Matching the document
export const workflowStages: WorkflowStage[] = [
  {
    id: "stage_1",
    stage_number: 1,
    name: "Lead Capture & Qualification",
    description: "Capture leads, assess urgency, budget, decision-makers and solution type (talent vs. app). Ops/Mgmt validates feasibility early.",
    owner: "Sales (Ops & Mgmt looped in as needed)",
    rationale: "Prevents chasing unqualified leads, avoids overpromising, ensures alignment with operational capabilities.",
    timeline: "Day 0-2",
    icon: "Target",
    color: "bg-purple-600",
    tasks: [
      "Capture lead information",
      "Assess urgency and budget",
      "Identify decision-makers",
      "Determine solution type (talent vs app)",
      "Ops/Mgmt feasibility validation"
    ]
  },
  {
    id: "stage_2",
    stage_number: 2,
    name: "Discovery & Solution Mapping",
    description: "Deep dive into client needs, identify delivery path (placement vs. app), document requirements in a standard intake form.",
    owner: "Sales + Mgt for strategic accounts",
    rationale: "Ensures both client expectations and operational feasibility are clear, minimizing surprises later.",
    timeline: "Day 2-5",
    icon: "Search",
    color: "bg-red-600",
    tasks: [
      "Conduct discovery calls",
      "Map client needs",
      "Define delivery path",
      "Complete intake form",
      "Strategic account review"
    ]
  },
  {
    id: "stage_3",
    stage_number: 3,
    name: "Proposal, Commercials & Approval",
    description: "Create proposal with pricing, timelines, SLAs, and resource readiness. Ops validates capacity, Mgmt signs off for risk/high-value accounts.",
    owner: "Sales drafts, Ops validates, Mgt approves strategic deals",
    rationale: "Prevents chasing unqualified leads, avoids overpromising, ensures alignment with operational capabilities.",
    timeline: "Day 5-7",
    icon: "FileText",
    color: "bg-orange-500",
    tasks: [
      "Draft proposal with pricing",
      "Define timelines and SLAs",
      "Assess resource readiness",
      "Ops capacity validation",
      "Management approval (strategic deals)"
    ]
  },
  {
    id: "stage_4",
    stage_number: 4,
    name: "Contracting & Kickoff",
    description: "Execute contracts, schedule kickoff call, introduce all teams, clarify responsibilities.",
    owner: "Sales closes → Ops prepares delivery → Mgmt oversees key accounts",
    rationale: "Smooth transition from sales to delivery, establishes accountability, ensures client confidence.",
    timeline: "Day 7-10",
    icon: "Handshake",
    color: "bg-amber-600",
    tasks: [
      "Execute contracts",
      "Schedule kickoff call",
      "Introduce all teams",
      "Clarify responsibilities",
      "Handoff documentation"
    ]
  },
  {
    id: "stage_5",
    stage_number: 5,
    name: "Delivery Execution & Adoption",
    description: "Ops leads delivery; Sales monitors adoption/feedback. For Talent: candidate sourcing, screening, interviews, selection, onboarding. For App: Training, adoption monitoring, ongoing support.",
    owner: "Ops leads delivery; Sales monitors adoption/feedback",
    rationale: "Structured, predictable delivery increases client satisfaction and adoption while keeping Ops and Sales coordinated.",
    timeline: "Day 10+",
    icon: "Rocket",
    color: "bg-green-600",
    tasks: [
      "Talent: Sourcing & screening",
      "Talent: Interviews & selection",
      "Talent: Onboarding",
      "App: Training delivery",
      "App: Adoption monitoring",
      "Ongoing support"
    ]
  },
  {
    id: "stage_6",
    stage_number: 6,
    name: "Feedback, Optimization & Growth",
    description: "Collect client feedback/NPS, resolve issues, suggest improvements. Conduct quarterly business reviews and identify upsell/cross-sell opportunities.",
    owner: "Joint (Sales, Ops, Mgt)",
    rationale: "Continuous improvement, reduces churn, maximizes client lifetime value, and creates long-term partnerships.",
    timeline: "Post-delivery, Quarterly",
    icon: "TrendingUp",
    color: "bg-teal-700",
    tasks: [
      "Collect client feedback/NPS",
      "Resolve issues",
      "Suggest improvements",
      "Quarterly business reviews",
      "Identify upsell opportunities",
      "Cross-sell analysis"
    ]
  }
];

// ==================== NEW SALES & CONVERSION DATA ====================

// Team Performance Data for GraphsComponent
export const teamPerformance = [
  {
    metric: "Lead Conversion Rate",
    sales: 65,
    target: 70,
    change: "+5%"
  },
  {
    metric: "Pipeline Velocity",
    sales: 42,
    target: 35,
    change: "-7%"
  },
  {
    metric: "Deal Size Average",
    sales: 85000000,
    target: 90000000,
    change: "+5.8%"
  },
  {
    metric: "Win Rate",
    sales: 35,
    target: 40,
    change: "+8%"
  },
  {
    metric: "Sales Cycle Length",
    sales: 45,
    target: 40,
    change: "+5%"
  }
];

// Sales Data by Time Periods
export const salesDataByMonth = [
  { month: "Jan", sales: 450000000, leads: 45, conversions: 12 },
  { month: "Feb", sales: 520000000, leads: 52, conversions: 15 },
  { month: "Mar", sales: 480000000, leads: 48, conversions: 14 },
  { month: "Apr", sales: 610000000, leads: 61, conversions: 18 },
  { month: "May", sales: 580000000, leads: 58, conversions: 17 },
  { month: "Jun", sales: 720000000, leads: 72, conversions: 22 },
  { month: "Jul", sales: 680000000, leads: 68, conversions: 20 },
  { month: "Aug", sales: 750000000, leads: 75, conversions: 25 },
  { month: "Sep", sales: 820000000, leads: 82, conversions: 28 },
  { month: "Oct", sales: 950000000, leads: 95, conversions: 32 },
  { month: "Nov", sales: 880000000, leads: 88, conversions: 30 },
  { month: "Dec", sales: 780000000, leads: 78, conversions: 26 }
];

export const salesDataByWeek = [
  { week: "W1 Oct", sales: 220000000, leads: 22, conversions: 7 },
  { week: "W2 Oct", sales: 180000000, leads: 18, conversions: 6 },
  { week: "W3 Oct", sales: 250000000, leads: 25, conversions: 8 },
  { week: "W4 Oct", sales: 300000000, leads: 30, conversions: 11 },
  { week: "W1 Nov", sales: 280000000, leads: 28, conversions: 9 },
  { week: "W2 Nov", sales: 320000000, leads: 32, conversions: 12 },
  { week: "W3 Nov", sales: 180000000, leads: 18, conversions: 5 },
  { week: "W4 Nov", sales: 100000000, leads: 10, conversions: 4 }
];

export const salesDataByDay = [
  { day: "Mon", sales: 45000000, leads: 8, conversions: 3 },
  { day: "Tue", sales: 52000000, leads: 9, conversions: 4 },
  { day: "Wed", sales: 48000000, leads: 7, conversions: 2 },
  { day: "Thu", sales: 61000000, leads: 11, conversions: 5 },
  { day: "Fri", sales: 38000000, leads: 6, conversions: 2 },
  { day: "Sat", sales: 15000000, leads: 3, conversions: 1 },
  { day: "Sun", sales: 8000000, leads: 2, conversions: 0 }
];

// Conversion Rate Data
export const conversionRateData = [
  { stage: "Leads Generated", count: 100, converted: 100, rate: 100 },
  { stage: "Initial Contact", count: 85, converted: 85, rate: 85 },
  { stage: "Needs Analysis", count: 65, converted: 65, rate: 76 },
  { stage: "Proposal Sent", count: 45, converted: 45, rate: 69 },
  { stage: "Negotiation", count: 30, converted: 30, rate: 67 },
  { stage: "Closed Won", count: 22, converted: 22, rate: 73 }
];

// Monthly Average Sales
export const monthlyAverageSales = {
  currentMonth: 950000000,
  previousMonth: 820000000,
  average: 678333333,
  growth: 15.8,
  trend: "up" as const
};

// Sales by Project Type with Conversion Rates
export const salesByProjectType = [
  {
    type: "Talent Placement",
    revenue: 3020000000,
    projects: 7,
    avgValue: 431428571,
    conversionRate: 68,
    leads: 45,
    closed: 31
  },
  {
    type: "App Deployment",
    revenue: 1980000000,
    projects: 4,
    avgValue: 495000000,
    conversionRate: 72,
    leads: 28,
    closed: 20
  }
];

// Sales Performance Metrics
export const salesPerformance = {
  totalRevenue: 5000000000,
  monthlyRecurring: 420000000,
  averageDealSize: 85000000,
  conversionRate: 68.5,
  salesCycle: 42,
  leadToCloseRate: 22
};

// Revenue by Project Type
export const revenueByProjectType = [
  { 
    type: "Talent Placement", 
    revenue: 3020000000,
    projects: 7, 
    avgValue: 431428571
  },
  { 
    type: "App Deployment", 
    revenue: 1980000000,
    projects: 4, 
    avgValue: 495000000
  }
];

// ==================== EXISTING MOCK DATA ====================

// COMBINED LEADS & PROJECTS DATA - All in one unified system
export const salesProjects: SalesProject[] = [
  // STAGE 1 PROJECTS (Leads) - Uganda-focused companies
  {
    id: "proj_lead_1",
    name: "Data Science Team Building",
    client: "Analytics Pro Uganda",
    type: "talent_placement",
    current_stage: 1,
    progress: 15,
    start_date: "2024-10-20",
    expected_completion: "2025-03-15",
    value: "UGX 275M",
    status: "on_track",
    team: {
      sales: ["Sarah Johnson"],
      ops: [],
      management: []
    },
    ownership_type: "company_owned",
    project_name_approved: false,
    is_lead: true,
    client_contact_info: {
      email: "john.davis@analyticspro.ug",
      phone: "+256-755-0123",
      city: "Kampala",
      country: "Uganda"
    },
    location: "Kampala, Uganda",
    currency: "UGX"
  },
  {
    id: "proj_lead_2",
    name: "App Modernization Project",
    client: "LegacySoft East Africa",
    type: "app_deployment",
    current_stage: 1,
    progress: 10,
    start_date: "2024-10-19",
    expected_completion: "2025-02-28",
    value: "UGX 350M",
    status: "on_track",
    team: {
      sales: ["Michael Chen"],
      ops: [],
      management: []
    },
    ownership_type: "company_owned",
    project_name_approved: false,
    is_lead: true,
    client_contact_info: {
      email: "maria.rodriguez@legacysoft.co.ug",
      phone: "+256-755-0124",
      city: "Kampala",
      country: "Uganda"
    },
    location: "Kampala, Uganda",
    currency: "UGX"
  },
  {
    id: "proj_lead_3",
    name: "Custom CRM Development",
    client: "SalesForce Uganda Ltd",
    type: "app_deployment",
    current_stage: 1,
    progress: 5,
    start_date: "2024-10-21",
    expected_completion: "2025-04-30",
    value: "UGX 295M",
    status: "on_track",
    team: {
      sales: ["Sarah Johnson"],
      ops: [],
      management: []
    },
    ownership_type: "company_owned",
    project_name_approved: false,
    is_lead: true,
    client_contact_info: {
      email: "robert.kim@salesforceuganda.com",
      phone: "+256-755-0125",
      city: "Kampala",
      country: "Uganda"
    },
    location: "Kampala, Uganda",
    currency: "UGX"
  },
  {
    id: "proj_6",
    name: "Cloud Infrastructure Talent Pool",
    client: "GlobalTech Uganda",
    type: "talent_placement",
    current_stage: 1,
    progress: 15,
    start_date: "2024-10-12",
    expected_completion: "2025-03-15",
    value: "UGX 665M",
    status: "on_track",
    team: {
      sales: ["Sarah Johnson"],
      ops: [],
      management: []
    },
    ownership_type: "company_owned",
    project_name_approved: false,
    is_lead: true,
    location: "Kampala, Uganda",
    currency: "UGX"
  },

  // STAGE 2 PROJECTS (Discovery)
  {
    id: "proj_5",
    name: "EdTech Talent Acquisition Program",
    client: "EduTech Systems Uganda",
    type: "talent_placement",
    current_stage: 2,
    progress: 25,
    start_date: "2024-10-15",
    expected_completion: "2025-02-28",
    value: "UGX 350M",
    status: "on_track",
    team: {
      sales: ["Michael Chen"],
      ops: ["Lisa Chen"],
      management: []
    },
    ownership_type: "partner_submitted",
    submitted_by: "user_6",
    client_contact_info: {
      email: "contact@edutechuganda.com",
      phone: "+256-755-0127",
      city: "Kampala",
      country: "Uganda"
    },
    project_name_approved: false,
    is_lead: false,
    location: "Kampala, Uganda",
    currency: "UGX"
  },
  {
    id: "proj_7",
    name: "Cloud Migration Talent Initiative",
    client: "CloudFirst Uganda Ltd",
    type: "talent_placement",
    current_stage: 2,
    progress: 20,
    start_date: "2024-10-20",
    expected_completion: "2025-03-01",
    value: "UGX 460M",
    status: "on_track",
    team: {
      sales: ["Michael Chen"],
      ops: ["David Kumar"],
      management: []
    },
    ownership_type: "partner_submitted",
    submitted_by: "user_6",
    client_contact_info: {
      email: "sarah.thompson@cloudfirst.ug",
      phone: "+256-755-0126",
      city: "Kampala",
      country: "Uganda"
    },
    project_name_approved: false,
    is_lead: false,
    location: "Kampala, Uganda",
    currency: "UGX"
  },

  // STAGE 3 PROJECTS (Proposal)
  {
    id: "proj_3",
    name: "Digital Transformation - Talent & App Bundle",
    client: "RetailMax Uganda Inc",
    type: "talent_placement",
    current_stage: 3,
    progress: 45,
    start_date: "2024-10-10",
    expected_completion: "2025-01-31",
    value: "UGX 665M",
    status: "at_risk",
    team: {
      sales: ["Sarah Johnson", "Michael Chen"],
      ops: ["David Kumar"],
      management: ["Sales Admin"]
    },
    ownership_type: "company_owned",
    project_name_approved: true,
    is_lead: false,
    location: "Kampala, Uganda",
    currency: "UGX"
  },

  // STAGE 4 PROJECTS (Contracting)
  {
    id: "proj_2",
    name: "Mobile App Deployment - FinTech Platform",
    client: "FinTech Innovations Uganda",
    type: "app_deployment",
    current_stage: 4,
    progress: 60,
    start_date: "2024-10-01",
    expected_completion: "2024-12-15",
    value: "UGX 535M",
    status: "on_track",
    team: {
      sales: ["Michael Chen"],
      ops: ["James Wilson", "Anna Martinez"],
      management: ["Sales Admin"]
    },
    ownership_type: "company_owned",
    project_name_approved: true,
    is_lead: false,
    location: "Kampala, Uganda",
    currency: "UGX"
  },

  // STAGE 5 PROJECTS (Delivery)
  {
    id: "proj_1",
    name: "Enterprise Talent Placement - Senior Engineers",
    client: "TechCorp Solutions Uganda",
    type: "talent_placement",
    current_stage: 5,
    progress: 75,
    start_date: "2024-09-15",
    expected_completion: "2024-11-30",
    value: "UGX 925M",
    status: "on_track",
    team: {
      sales: ["Sarah Johnson"],
      ops: ["David Kumar", "Lisa Chen"],
      management: ["Sales Admin"]
    },
    ownership_type: "company_owned",
    project_name_approved: true,
    is_lead: false,
    location: "Kampala, Uganda",
    currency: "UGX"
  },

  // STAGE 6 PROJECTS (Optimization)
  {
    id: "proj_4",
    name: "Healthcare App Implementation",
    client: "HealthTech Solutions Uganda",
    type: "app_deployment",
    current_stage: 6,
    progress: 95,
    start_date: "2024-08-01",
    expected_completion: "2024-10-30",
    value: "UGX 445M",
    status: "on_track",
    team: {
      sales: ["Sarah Johnson"],
      ops: ["Anna Martinez"],
      management: ["Sales Admin"]
    },
    ownership_type: "company_owned",
    project_name_approved: true,
    is_lead: false,
    location: "Kampala, Uganda",
    currency: "UGX"
  }
];

// PROJECT NAME APPROVALS
export const mockProjectNameApprovals: ProjectNameApproval[] = [
  {
    id: "approval_1",
    project_id: "proj_3",
    requested_name: "Digital Transformation - Talent & App Bundle",
    requested_by: "user_2",
    approved: true,
    approved_by: "user_1",
    approved_at: "2024-10-15T14:00:00Z",
    comments: "Name approved for contracting stage"
  },
  {
    id: "approval_2",
    project_id: "proj_7",
    requested_name: "Cloud Migration Talent Initiative",
    requested_by: "user_6",
    approved: false,
    comments: "Pending management review - name too generic"
  }
];

// STAGE GATES
export const mockStageGates: StageGate[] = [
  {
    project_id: "proj_3",
    stage: 3,
    requirements: {
      client_info_required: true,
      project_name_required: false,
      budget_approved: true,
      contract_signed: false
    },
    approvals: [
      {
        id: "app_1",
        type: "budget",
        approved: true,
        approved_by: "user_1",
        approved_at: "2024-10-16T10:00:00Z",
        comments: "Budget within acceptable range"
      }
    ],
    blockers: ["Waiting for client signature on proposal"]
  }
];

// USER ROLES - Enhanced with new permissions
export const mockUserRoles: { [userId: string]: UserRole } = {
  user_1: {
    can_create_leads: true,
    can_submit_projects: true,
    can_approve_project_names: true,
    can_move_to_contracting: true,
    view_company_owned_only: false,
    view_all_projects: true,
    can_manage_documents: true,
    can_view_financials: true
  },
  user_2: {
    can_create_leads: true,
    can_submit_projects: true,
    can_approve_project_names: false,
    can_move_to_contracting: true,
    view_company_owned_only: false,
    view_all_projects: true,
    can_manage_documents: true,
    can_view_financials: true
  },
  user_6: {
    can_create_leads: true,
    can_submit_projects: true,
    can_approve_project_names: false,
    can_move_to_contracting: true,
    view_company_owned_only: false,
    view_all_projects: true,
    can_manage_documents: true,
    can_view_financials: true
  },
  user_4: {
    can_create_leads: false,
    can_submit_projects: false,
    can_approve_project_names: false,
    can_move_to_contracting: false,
    view_company_owned_only: true,
    view_all_projects: false,
    can_manage_documents: false,
    can_view_financials: false
  }
};

// USERS - Updated with Uganda context
export const mockUsers: User[] = [
  {
    id: "user_1",
    name: "Sales Admin",
    email: "sales@proapp.ug",
    role: "super_admin",
    first_name: "Sales",
    last_name: "Admin",
    profile_pic: null,
    is_verified: true,
    created_at: "2024-01-01T00:00:00Z"
  },
  {
    id: "user_2",
    name: "Sarah Johnson",
    email: "sarah.johnson@proapp.ug",
    role: "manager",
    first_name: "Sarah",
    last_name: "Johnson",
    profile_pic: null,
    is_verified: true,
    created_at: "2024-01-15T00:00:00Z"
  },
  {
    id: "user_3",
    name: "David Kumar",
    email: "david.kumar@proapp.ug",
    role: "project_manager",
    first_name: "David",
    last_name: "Kumar",
    profile_pic: null,
    is_verified: true,
    created_at: "2024-01-20T00:00:00Z"
  },
  {
    id: "user_4",
    name: "Lisa Chen",
    email: "lisa.chen@proapp.ug",
    role: "user",
    first_name: "Lisa",
    last_name: "Chen",
    profile_pic: null,
    is_verified: true,
    created_at: "2024-02-01T00:00:00Z"
  },
  {
    id: "user_5",
    name: "James Wilson",
    email: "james.wilson@proapp.ug",
    role: "project_manager",
    first_name: "James",
    last_name: "Wilson",
    profile_pic: null,
    is_verified: true,
    created_at: "2024-02-10T00:00:00Z"
  },
  {
    id: "user_6",
    name: "Michael Chen",
    email: "michael.chen@proapp.ug",
    role: "manager",
    first_name: "Michael",
    last_name: "Chen",
    profile_pic: null,
    is_verified: true,
    created_at: "2024-02-15T00:00:00Z"
  },
  {
    id: "user_7",
    name: "Anna Martinez",
    email: "anna.martinez@proapp.ug",
    role: "user",
    first_name: "Anna",
    last_name: "Martinez",
    profile_pic: null,
    is_verified: true,
    created_at: "2024-03-01T00:00:00Z"
  }
];

// UPDATED SALES METRICS - Including leads in count with UGX values
export const mockSalesMetrics: SalesMetric[] = [
  {
    id: "1",
    title: "Total Projects & Leads",
    value: "11",
    change: "+57%",
    icon: "Briefcase",
    trend: "up"
  },
  {
    id: "2",
    title: "Total Pipeline Value",
    value: "UGX 5B",
    change: "+38%",
    icon: "DollarSign",
    trend: "up"
  },
  {
    id: "3",
    title: "Active Leads (Stage 1)",
    value: "4",
    change: "+33%",
    icon: "Target",
    trend: "up"
  },
  {
    id: "4",
    title: "On-Time Delivery Rate",
    value: "87%",
    change: "-3%",
    icon: "Clock",
    trend: "down"
  }
];

// UPDATED PIPELINE BY STAGE - Including leads in Stage 1 with UGX values
export const mockSalesPipeline: PipelineStage[] = [
  {
    id: "1",
    name: "Lead Capture & Qualification",
    count: 4,
    value: "UGX 1.58B",
    color: "bg-purple-600",
    deals: [
      { name: "Cloud Infrastructure Talent Pool", value: "UGX 665M", company: "GlobalTech Uganda" },
      { name: "Data Science Team Building", value: "UGX 275M", company: "Analytics Pro Uganda" },
      { name: "App Modernization Project", value: "UGX 350M", company: "LegacySoft East Africa" },
      { name: "Custom CRM Development", value: "UGX 295M", company: "SalesForce Uganda Ltd" }
    ]
  },
  {
    id: "2",
    name: "Discovery & Solution Mapping",
    count: 2,
    value: "UGX 810M",
    color: "bg-red-600",
    deals: [
      { name: "EdTech Talent Acquisition Program", value: "UGX 350M", company: "EduTech Systems Uganda" },
      { name: "Cloud Migration Talent Initiative", value: "UGX 460M", company: "CloudFirst Uganda Ltd" }
    ]
  },
  {
    id: "3",
    name: "Proposal & Approval",
    count: 1,
    value: "UGX 665M",
    color: "bg-orange-500",
    deals: [
      { name: "Digital Transformation Bundle", value: "UGX 665M", company: "RetailMax Uganda Inc" }
    ]
  },
  {
    id: "4",
    name: "Contracting & Kickoff",
    count: 1,
    value: "UGX 535M",
    color: "bg-amber-600",
    deals: [
      { name: "FinTech App Platform", value: "UGX 535M", company: "FinTech Innovations Uganda" }
    ]
  },
  {
    id: "5",
    name: "Delivery & Execution",
    count: 1,
    value: "UGX 925M",
    color: "bg-green-600",
    deals: [
      { name: "Enterprise Engineer Placement", value: "UGX 925M", company: "TechCorp Solutions Uganda" }
    ]
  },
  {
    id: "6",
    name: "Optimization & Growth",
    count: 1,
    value: "UGX 445M",
    color: "bg-teal-700",
    deals: [
      { name: "Healthcare App Implementation", value: "UGX 445M", company: "HealthTech Solutions Uganda" }
    ]
  }
];

// UPDATED TOP DEALS - Including leads with UGX values
export const mockTopDeals: TopDeal[] = [
  {
    id: "1",
    name: "Enterprise Talent Placement - Senior Engineers",
    company: "TechCorp Solutions Uganda",
    value: "UGX 925M",
    stage: "Delivery & Execution",
    probability: 95,
    closeDate: "Nov 30, 2024",
    owner: "Sarah Johnson"
  },
  {
    id: "2",
    name: "Cloud Infrastructure Talent Pool",
    company: "GlobalTech Uganda",
    value: "UGX 665M",
    stage: "Lead Qualification",
    probability: 65,
    closeDate: "Mar 15, 2025",
    owner: "Sarah Johnson"
  },
  {
    id: "3",
    name: "Digital Transformation - Talent & App Bundle",
    company: "RetailMax Uganda Inc",
    value: "UGX 665M",
    stage: "Proposal & Approval",
    probability: 70,
    closeDate: "Jan 31, 2025",
    owner: "Michael Chen"
  },
  {
    id: "4",
    name: "Mobile App Deployment - FinTech Platform",
    company: "FinTech Innovations Uganda",
    value: "UGX 535M",
    stage: "Contracting & Kickoff",
    probability: 85,
    closeDate: "Dec 15, 2024",
    owner: "Michael Chen"
  },
  {
    id: "5",
    name: "Cloud Migration Talent Initiative",
    company: "CloudFirst Uganda Ltd",
    value: "UGX 460M",
    stage: "Discovery & Solution Mapping",
    probability: 60,
    closeDate: "Mar 01, 2025",
    owner: "Michael Chen"
  }
];

// UPDATED ACTIVITY FEED - Focus on lead-to-project progression
export const mockRecentActivity: Activity[] = [
  {
    id: "1",
    type: "stage_update",
    description: "New lead progressed to Stage 2 - CloudFirst Uganda Ltd",
    user: "Michael Chen",
    timestamp: "2 hours ago",
    status: "completed"
  },
  {
    id: "2",
    type: "lead_created",
    description: "New lead captured - Analytics Pro Uganda (Data Science Team)",
    user: "Sarah Johnson",
    timestamp: "4 hours ago",
    status: "completed"
  },
  {
    id: "3",
    type: "deal_update",
    description: "FinTech Platform moved to Contracting & Kickoff (Stage 4)",
    user: "Michael Chen",
    timestamp: "6 hours ago",
    status: "completed"
  },
  {
    id: "4",
    type: "approval_request",
    description: "Project name approval requested - Cloud Migration Talent Initiative",
    user: "Michael Chen",
    timestamp: "1 day ago",
    status: "pending"
  },
  {
    id: "5",
    type: "stage_update",
    description: "Lead qualified and moved to Discovery - LegacySoft East Africa",
    user: "Michael Chen",
    timestamp: "1 day ago",
    status: "completed"
  },
  {
    id: "6",
    type: "lead_created",
    description: "New lead captured - SalesForce Uganda Ltd (Custom CRM)",
    user: "Sarah Johnson",
    timestamp: "2 days ago",
    status: "completed"
  },
  {
    id: "7",
    type: "deal_update",
    description: "RetailMax project approved for Stage 3 - Proposal",
    user: "Sarah Johnson",
    timestamp: "2 days ago",
    status: "completed"
  },
  {
    id: "8",
    type: "stage_update",
    description: "EduTech Systems Uganda progressed to Stage 2 - Discovery",
    user: "Michael Chen",
    timestamp: "3 days ago",
    status: "completed"
  },
  {
    id: "9",
    type: "deal_update",
    description: "TechCorp placement reached Stage 5 - Delivery",
    user: "Sarah Johnson",
    timestamp: "3 days ago",
    status: "completed"
  },
  {
    id: "10",
    type: "lead_created",
    description: "New lead captured - GlobalTech Uganda",
    user: "Sarah Johnson",
    timestamp: "4 days ago",
    status: "completed"
  }
];

// UPDATED STATS with UGX values
export const mockStats: SalesStats = {
  assignedBusinesses: 35,
  totalSales: 5000000000,
  totalDeals: 11,
  businessGrowth: 35,
  salesGrowth: 38,
  dealGrowth: 57
};

// BUSINESSES (Clients) - Updated with Uganda companies
export const mockBusinesses: Business[] = [
  {
    id: "1",
    name: "TechCorp Solutions Uganda",
    category: "Technology",
    description: "Enterprise software solutions - Talent placement in progress",
    employee_count: 500,
    status: "active",
    created_at: "2024-09-15",
    icon: "Laptop"
  },
  {
    id: "2", 
    name: "RetailMax Uganda Inc",
    category: "Retail",
    description: "Digital transformation project - Proposal stage",
    employee_count: 300,
    status: "active",
    created_at: "2024-10-10",
    icon: "ShoppingCart"
  },
  {
    id: "3",
    name: "FinTech Innovations Uganda", 
    category: "Finance",
    description: "Mobile app deployment - Kickoff phase",
    employee_count: 150,
    status: "active",
    created_at: "2024-10-01",
    icon: "DollarSign"
  },
  {
    id: "4",
    name: "HealthTech Solutions Uganda",
    category: "Healthcare", 
    description: "App implementation complete - Optimization phase",
    employee_count: 200,
    status: "active",
    created_at: "2024-08-01",
    icon: "Activity"
  },
  {
    id: "5",
    name: "EduTech Systems Uganda",
    category: "Education",
    description: "Talent acquisition - Discovery phase", 
    employee_count: 80,
    status: "active",
    created_at: "2024-10-15",
    icon: "BookOpen"
  },
  {
    id: "6",
    name: "GlobalTech Uganda",
    category: "Technology",
    description: "Enterprise talent pool - Qualification stage",
    employee_count: 750,
    status: "active",
    created_at: "2024-10-12",
    icon: "Globe"
  },
  {
    id: "7",
    name: "CloudFirst Uganda Ltd",
    category: "Cloud Services",
    description: "Cloud migration talent - Discovery phase",
    employee_count: 220,
    status: "active",
    created_at: "2024-10-08",
    icon: "Cloud"
  },
  {
    id: "8",
    name: "Analytics Pro Uganda",
    category: "Technology",
    description: "Data science team building - Lead qualification",
    employee_count: 120,
    status: "active",
    created_at: "2024-10-20",
    icon: "BarChart"
  },
  {
    id: "9",
    name: "LegacySoft East Africa",
    category: "Technology",
    description: "App modernization - Lead qualification",
    employee_count: 450,
    status: "active",
    created_at: "2024-10-19",
    icon: "Code"
  },
  {
    id: "10",
    name: "SalesForce Uganda Ltd",
    category: "Technology",
    description: "Custom CRM development - Lead qualification",
    employee_count: 180,
    status: "active",
    created_at: "2024-10-21",
    icon: "Database"
  }
];

// UPDATED NOTIFICATIONS
export const mockNotifications: Notification[] = [
  {
    id: 1,
    title: "New lead requires qualification",
    message: "Analytics Pro Uganda - Data Science Team needs urgency assessment",
    time: "30 minutes ago",
    type: "warning",
    unread: true
  },
  {
    id: 2,
    title: "Lead progressed to Stage 2",
    message: "CloudFirst Uganda Ltd moved from Lead Capture to Discovery",
    time: "2 hours ago",
    type: "success",
    unread: true
  },
  {
    id: 3,
    title: "Project name approval needed",
    message: "Cloud Migration Talent Initiative requires name approval",
    time: "1 day ago",
    type: "approval",
    unread: false
  },
  {
    id: 4,
    title: "Stage transition completed",
    message: "FinTech Platform moved to Contracting & Kickoff",
    time: "1 day ago",
    type: "info",
    unread: false
  },
  {
    id: 5,
    title: "New lead captured",
    message: "SalesForce Uganda Ltd - Custom CRM development",
    time: "2 days ago",
    type: "info",
    unread: false
  },
  {
    id: 6,
    title: "Lead qualification due",
    message: "3 leads pending qualification review",
    time: "2 days ago",
    type: "warning",
    unread: false
  }
];

// AUTH USER
export const mockAuthUser: AuthUser = {
  email: "sales@proapp.ug",
  password: "sales@2025",
  username: "Sales Admin",
  role: "super_admin"
};

// UPDATED GROUPS - Matching collaboration themes
export const mockGroups: Group[] = [
  {
    group_id: "grp_1",
    name: "Sales-Ops-Management Collaboration",
    description: "Cross-functional project coordination and workflow discussions",
    icon_image_url: null,
    banner_image_url: null,
    members: 12,
    membership_type: "private",
    created_at: "2024-09-01T10:00:00Z",
    created_by: "user_1",
    role: "admin"
  },
  {
    group_id: "grp_2",
    name: "Lead Qualification Team",
    description: "Coordination for lead assessment and stage progression",
    icon_image_url: null,
    banner_image_url: null,
    members: 8,
    membership_type: "private",
    created_at: "2024-09-10T14:00:00Z",
    created_by: "user_1",
    role: "admin"
  },
  {
    group_id: "grp_3",
    name: "App Deployment & Support",
    description: "App delivery, training, and adoption monitoring",
    icon_image_url: null,
    banner_image_url: null,
    members: 6,
    membership_type: "private",
    created_at: "2024-09-15T09:00:00Z",
    created_by: "user_2",
    role: "member"
  },
  {
    group_id: "grp_4",
    name: "Strategic Accounts",
    description: "High-value account management and quarterly reviews",
    icon_image_url: null,
    banner_image_url: null,
    members: 5,
    membership_type: "private",
    created_at: "2024-08-20T11:00:00Z",
    created_by: "user_1",
    role: "admin"
  }
];

// UPDATED GROUP MESSAGES - Focus on lead progression
export const mockGroupMessages: GroupMessages = {
  grp_1: [
    {
      messageId: "msg_1",
      text: "Team, we need to prioritize lead qualification this week. 4 new leads in Stage 1 require assessment.",
      senderUserName: "Sales Admin",
      senderId: "user_1",
      timestamp: "2024-10-21T09:30:00Z",
      status: "read",
      conversationId: "grp_1",
      receiverId: "grp_1",
      media: null
    },
    {
      messageId: "msg_2",
      text: "I'm working on Analytics Pro Uganda and SalesForce Uganda Ltd leads. Both show strong potential for Stage 2 progression.",
      senderUserName: "Sarah Johnson",
      senderId: "user_2",
      timestamp: "2024-10-21T09:45:00Z",
      status: "read",
      conversationId: "grp_1",
      receiverId: "grp_1",
      media: null
    },
    {
      messageId: "msg_3",
      text: "CloudFirst Uganda Ltd has been moved to Stage 2. Client is very engaged and budget is confirmed.",
      senderUserName: "Michael Chen",
      senderId: "user_6",
      timestamp: "2024-10-21T10:00:00Z",
      status: "read",
      conversationId: "grp_1",
      receiverId: "grp_1",
      media: null
    }
  ],
  grp_2: [
    {
      messageId: "msg_4",
      text: "Quick update on lead qualifications: Analytics Pro Uganda - High urgency, LegacySoft East Africa - Medium, SalesForce Uganda Ltd - Low",
      senderUserName: "Sarah Johnson",
      senderId: "user_2",
      timestamp: "2024-10-21T08:15:00Z",
      status: "read",
      conversationId: "grp_2",
      receiverId: "grp_2",
      media: null
    },
    {
      messageId: "msg_5",
      text: "GlobalTech Uganda lead looks very promising. UGX 665M budget and clear decision-makers. Ready for Stage 2.",
      senderUserName: "Sarah Johnson",
      senderId: "user_2",
      timestamp: "2024-10-21T08:30:00Z",
      status: "read",
      conversationId: "grp_2",
      receiverId: "grp_2",
      media: null
    },
    {
      messageId: "msg_6",
      text: "I'll take LegacySoft East Africa through discovery calls this week. Their app modernization needs align well with our capabilities.",
      senderUserName: "Michael Chen",
      senderId: "user_6",
      timestamp: "2024-10-21T10:00:00Z",
      status: "read",
      conversationId: "grp_2",
      receiverId: "grp_2",
      media: null
    }
  ],
  grp_3: [
    {
      messageId: "msg_7",
      text: "FinTech app kickoff scheduled for Oct 22. Successfully transitioned from Stage 3 to Stage 4.",
      senderUserName: "James Wilson",
      senderId: "user_5",
      timestamp: "2024-10-21T07:00:00Z",
      status: "read",
      conversationId: "grp_3",
      receiverId: "grp_3",
      media: null
    },
    {
      messageId: "msg_8",
      text: "Perfect! Smooth transition from lead to contracting stage. This sets a good precedent for our new workflow.",
      senderUserName: "Michael Chen",
      senderId: "user_6",
      timestamp: "2024-10-21T07:20:00Z",
      status: "read",
      conversationId: "grp_3",
      receiverId: "grp_3",
      media: null
    }
  ],
  grp_4: [
    {
      messageId: "msg_9",
      text: "HealthTech quarterly review completed. Successfully maintained through all 6 stages from initial lead to optimization.",
      senderUserName: "Anna Martinez",
      senderId: "user_7",
      timestamp: "2024-10-20T16:00:00Z",
      status: "read",
      conversationId: "grp_4",
      receiverId: "grp_4",
      media: null
    },
    {
      messageId: "msg_10",
      text: "Excellent example of our complete workflow. From Stage 1 lead capture to Stage 6 optimization and growth.",
      senderUserName: "Sales Admin",
      senderId: "user_1",
      timestamp: "2024-10-20T16:30:00Z",
      status: "read",
      conversationId: "grp_4",
      receiverId: "grp_4",
      media: null
    }
  ]
};

// GROUP MEMBERS
export const mockGroupMembers: GroupMembers = {
  grp_1: [
    {
      user_id: "user_1",
      first_name: "Sales",
      last_name: "Admin",
      email: "sales@proapp.ug",
      profile_pic: null,
      role: "admin",
      joined_at: "2024-09-01T10:00:00Z"
    },
    {
      user_id: "user_2",
      first_name: "Sarah",
      last_name: "Johnson",
      email: "sarah.johnson@proapp.ug",
      profile_pic: null,
      role: "member",
      joined_at: "2024-09-01T10:00:00Z"
    },
    {
      user_id: "user_6",
      first_name: "Michael",
      last_name: "Chen",
      email: "michael.chen@proapp.ug",
      profile_pic: null,
      role: "member",
      joined_at: "2024-09-01T10:00:00Z"
    }
  ],
  grp_2: [
    {
      user_id: "user_2",
      first_name: "Sarah",
      last_name: "Johnson",
      email: "sarah.johnson@proapp.ug",
      profile_pic: null,
      role: "admin",
      joined_at: "2024-09-10T14:00:00Z"
    },
    {
      user_id: "user_6",
      first_name: "Michael",
      last_name: "Chen",
      email: "michael.chen@proapp.ug",
      profile_pic: null,
      role: "member",
      joined_at: "2024-09-10T14:00:00Z"
    }
  ],
  grp_3: [
    {
      user_id: "user_5",
      first_name: "James",
      last_name: "Wilson",
      email: "james.wilson@proapp.ug",
      profile_pic: null,
      role: "admin",
      joined_at: "2024-09-15T09:00:00Z"
    },
    {
      user_id: "user_6",
      first_name: "Michael",
      last_name: "Chen",
      email: "michael.chen@proapp.ug",
      profile_pic: null,
      role: "member",
      joined_at: "2024-09-15T09:00:00Z"
    },
    {
      user_id: "user_7",
      first_name: "Anna",
      last_name: "Martinez",
      email: "anna.martinez@proapp.ug",
      profile_pic: null,
      role: "member",
      joined_at: "2024-09-15T09:00:00Z"
    }
  ],
  grp_4: [
    {
      user_id: "user_1",
      first_name: "Sales",
      last_name: "Admin",
      email: "sales@proapp.ug",
      profile_pic: null,
      role: "admin",
      joined_at: "2024-08-20T11:00:00Z"
    },
    {
      user_id: "user_2",
      first_name: "Sarah",
      last_name: "Johnson",
      email: "sarah.johnson@proapp.ug",
      profile_pic: null,
      role: "member",
      joined_at: "2024-08-20T11:00:00Z"
    },
    {
      user_id: "user_7",
      first_name: "Anna",
      last_name: "Martinez",
      email: "anna.martinez@proapp.ug",
      profile_pic: null,
      role: "member",
      joined_at: "2024-08-20T11:00:00Z"
    }
  ]
};

// HELPER FUNCTIONS FOR UNIFIED PROJECT/LEAD MANAGEMENT
export const projectHelpers = {
  // Get all projects/leads for a specific stage
  getProjectsByStage: (stage: number): SalesProject[] => {
    return salesProjects.filter(project => project.current_stage === stage);
  },

  // Get only leads (Stage 1 projects)
  getLeads: (): SalesProject[] => {
    return salesProjects.filter(project => project.current_stage === 1);
  },

  // Get active projects (Stage 2-6)
  getActiveProjects: (): SalesProject[] => {
    return salesProjects.filter(project => project.current_stage > 1);
  },

  // Check if project can have a name based on stage
  canHaveProjectName: (stage: number): boolean => {
    return stageValidationRules[stage as keyof typeof stageValidationRules]?.project_name_allowed || false;
  },

  // Move project to next stage with validation
  moveToNextStage: (projectId: string, targetStage: number): boolean => {
    const project = salesProjects.find(p => p.id === projectId);
    if (!project) return false;

    // Validate stage transition
    if (targetStage < 1 || targetStage > 6) return false;
    
    // Special validation for moving to Stage 4 (requires project name approval)
    if (targetStage === 4 && !project.project_name_approved) {
      return false;
    }

    project.current_stage = targetStage;
    project.progress = Math.min(100, (targetStage / 6) * 100);
    
    return true;
  },

  // Request project name approval
  requestProjectNameApproval: (projectId: string, requestedName: string, requestedBy: string): boolean => {
    const project = salesProjects.find(p => p.id === projectId);
    if (!project || project.current_stage < 3) return false;

    // Create approval request
    const approval: ProjectNameApproval = {
      id: `approval_${Date.now()}`,
      project_id: projectId,
      requested_name: requestedName,
      requested_by: requestedBy,
      approved: false,
      comments: "Pending approval"
    };

    // In a real app, this would be added to the approvals array
    console.log('Approval requested:', approval);
    return true;
  }
};

// GRAPH DATA FOR DASHBOARD - Updated for unified leads/projects

// Stage completion over time (for line chart)
export const stageCompletionTrend = [
  { month: "Jun", stage1: 8, stage2: 6, stage3: 4, stage4: 3, stage5: 2, stage6: 1 },
  { month: "Jul", stage1: 10, stage2: 7, stage3: 5, stage4: 4, stage5: 3, stage6: 2 },
  { month: "Aug", stage1: 12, stage2: 9, stage3: 6, stage4: 5, stage5: 4, stage6: 3 },
  { month: "Sep", stage1: 15, stage2: 11, stage3: 8, stage4: 6, stage5: 5, stage6: 4 },
  { month: "Oct", stage1: 18, stage2: 13, stage3: 9, stage4: 7, stage5: 6, stage6: 5 }
];

// Project status distribution (for pie/donut chart)
export const projectStatusDistribution = [
  { status: "On Track", count: 8, percentage: 72.7, color: "#10b981" },
  { status: "At Risk", count: 2, percentage: 18.2, color: "#f59e0b" },
  { status: "Delayed", count: 1, percentage: 9.1, color: "#ef4444" }
];

// Stage conversion rates (for funnel chart) - Updated for lead-to-project flow
export const stageConversionRates = [
  { stage: "Lead Capture (Stage 1)", count: 15, percentage: 100, converted: 11 },
  { stage: "Discovery (Stage 2)", count: 11, percentage: 73, converted: 8 },
  { stage: "Proposal (Stage 3)", count: 8, percentage: 53, converted: 6 },
  { stage: "Contracting (Stage 4)", count: 6, percentage: 40, converted: 5 },
  { stage: "Delivery (Stage 5)", count: 5, percentage: 33, converted: 4 },
  { stage: "Optimization (Stage 6)", count: 4, percentage: 27, converted: 3 }
];

// Lead-to-project conversion timeline (for area chart)
export const leadConversionTimeline = [
  { week: "Week 1", new_leads: 3, converted: 1, active_leads: 2 },
  { week: "Week 2", new_leads: 4, converted: 2, active_leads: 4 },
  { week: "Week 3", new_leads: 5, converted: 3, active_leads: 6 },
  { week: "Week 4", new_leads: 3, converted: 2, active_leads: 7 }
];

// Project value by stage (for stacked bar chart)
export const projectValueByStage = [
  { 
    stage: "Stage 1 - Leads", 
    talent: 940000000, 
    app: 645000000, 
    total: 1585000000 
  },
  { 
    stage: "Stage 2 - Discovery", 
    talent: 810000000, 
    app: 0, 
    total: 810000000 
  },
  { 
    stage: "Stage 3 - Proposal", 
    talent: 665000000, 
    app: 0, 
    total: 665000000 
  },
  { 
    stage: "Stage 4 - Contracting", 
    talent: 0, 
    app: 535000000, 
    total: 535000000 
  },
  { 
    stage: "Stage 5 - Delivery", 
    talent: 925000000, 
    app: 0, 
    total: 925000000 
  },
  { 
    stage: "Stage 6 - Optimization", 
    talent: 0, 
    app: 445000000, 
    total: 445000000 
  }
];

// Stage progression velocity (avg days in each stage)
export const stageProgressionVelocity = [
  { stage: "Lead Qualification", avgDays: 2, target: 2, variance: 0 },
  { stage: "Discovery", avgDays: 3, target: 3, variance: 0 },
  { stage: "Proposal", avgDays: 4, target: 2, variance: 2 },
  { stage: "Contracting", avgDays: 3, target: 3, variance: 0 },
  { stage: "Delivery", avgDays: 45, target: 40, variance: 5 },
  { stage: "Optimization", avgDays: 90, target: 90, variance: 0 }
];

export default {
  workflowStages,
  salesProjects,
  mockUsers,
  mockSalesMetrics,
  mockSalesPipeline,
  mockTopDeals,
  mockRecentActivity,
  mockStats,
  mockBusinesses,
  mockNotifications,
  mockAuthUser,
  mockGroups,
  mockGroupMessages,
  mockGroupMembers,
  projectHelpers,
  stageValidationRules,
  mockProjectNameApprovals,
  mockStageGates,
  mockUserRoles,
  
  // NEW EXPORTS FOR SALES ANALYTICS
  teamPerformance,
  salesDataByMonth,
  salesDataByWeek,
  salesDataByDay,
  conversionRateData,
  monthlyAverageSales,
  salesByProjectType,
  salesPerformance,
  revenueByProjectType,
  
  // EXISTING GRAPH EXPORTS
  stageCompletionTrend,
  projectStatusDistribution,
  stageConversionRates,
  projectValueByStage,
  leadConversionTimeline,
  stageProgressionVelocity
};